<template>
  <div class="correlations-container">
    <div class="container-filters">
      <form class="search-filters" @submit.prevent="onApplyFilters">
        <label>
          Tratament
          <input v-model="localFilters.treatment" placeholder="Tratament" type="text">
        </label>
        <label>
          Medici
          <select :class="{ 'has-value': localFilters.doctor }" v-model="localFilters.doctor">
            <option :value="null">-</option>
            <option v-for="Doctor in Doctors" :key="Doctor" :value="Doctor">{{ Doctor }}</option>
          </select>
        </label>
        <label>
          Gen
          <select v-model="localFilters.gender" :class="{ 'has-value': localFilters.gender }">
            <option
              v-for="option in genderOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label>
          Grupa vârstă
          <select v-model="localFilters.ageGroup" :class="{ 'has-value': localFilters.ageGroup }">
            <option
              v-for="group in ageGroups"
              :key="group.value"
              :value="group.value"
            >
              {{ group.label }}
            </option>
          </select>
        </label>
        <label>
          Dată de la
          <input v-model="localFilters.dateFrom" type="date">
        </label>
        <label>
          Dată până la
          <input v-model="localFilters.dateTo" type="date">
        </label>
        <label>
          Ultimele luni
          <select v-model.number="localFilters.lastMonths">
            <option :value="null">-</option>
            <option v-for="month in monthOptions" :key="'last-' + month" :value="month">{{ month }} luni</option>
          </select>
        </label>
        <button type="submit" class="search-btn">
          <img src="../assets/icons2/search-icon.svg" alt="search-icon">
        </button>
      </form>

      <div class="action-bar">
        <button  class="download-btn" @click="$emit('download', localFilters)" :disabled="isDownloading || isLoading">
          <span v-if="!isDownloading">📥 Descarcă Excel</span>
          <span v-else class="btn-spinner"></span>
        </button>
      </div>
    </div>

    <div class="table-wrapper" :class="{ 'is-loading': isLoading }">
      <div v-if="isLoading" class="table-loader-overlay">
        <div class="spinner"></div>
        <p>Se încarcă datele...</p>
      </div>

      <table class="table">
        <thead>
        <tr>
          <th>#</th>
          <th>Denumire tratament</th>
          <th>Total</th>
          <th>Bărbați</th>
          <th>Femei</th>
          <th>Necunoscut</th>
          <th>Vârstă dominantă</th>
          <th>Oraș principal</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="items.length === 0">
          <td colspan="8" class="no-data">Nu există date pentru filtrele selectate</td>
        </tr>
        <tr v-else v-for="(item, index) in items" :key="index">
          <td>{{ index + 1 }}</td>
          <td>{{ item.denumire }}</td>
          <td>{{ item.total }}</td>
          <td>
            {{ getSexData(item.sexDistribution, 'male').count }}
            ({{ getSexData(item.sexDistribution, 'male').percent }}%)
          </td>
          <td>
            {{ getSexData(item.sexDistribution, 'female').count }}
            ({{ getSexData(item.sexDistribution, 'female').percent }}%)
          </td>
          <td>
            {{ getSexData(item.sexDistribution, 'unknown').count }}
            ({{ getSexData(item.sexDistribution, 'unknown').percent }}%)
          </td>
          <td>{{ getTopAgeGroup(item.ageDistribution) }}</td>
          <td>{{ getTopLocation(item.topLocations) }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="summary">
      <span>Total tipuri de tratamente: <b>{{ items.length }}</b></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TreatmentsCorrelations',

  props: {
    items: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isDownloading: {
      type: Boolean,
      default: false
    },
    Doctors: {
      type: Array,
      default: () => []
    }
  },

  emits: ['apply-filters', 'download'],

  data () {
    return {
      monthOptions: [3, 6, 12],
      sexColumns: [
        { key: 'male', label: 'Bărbați' },
        { key: 'female', label: 'Femei' },
        { key: 'unknown', label: 'Necunoscut' }
      ],
      genderOptions: [
        { value: '', label: 'Toate' },
        { value: 'M', label: 'Masculin' },
        { value: 'F', label: 'Feminin' }
      ],
      ageGroups: [
        { value: '', label: 'Toate' },
        { value: '0-18', label: '0-18 ani' },
        { value: '19-30', label: '19-30 ani' },
        { value: '31-40', label: '31-40 ani' },
        { value: '41-50', label: '41-50 ani' },
        { value: '51-60', label: '51-60 ani' },
        { value: '61+', label: '61+ ani' }
      ],
      localFilters: {
        treatment: '',
        doctor: null,
        gender: '',
        ageGroup: '',
        dateFrom: '',
        dateTo: '',
        lastMonths: null,
      }
    }
  },

  watch: {
    'localFilters.lastMonths' (val) {
      if (val != null) {
        this.localFilters.dateFrom = ''
        this.localFilters.dateTo = ''
      }
    }
  },

  methods: {
    onApplyFilters () {
      this.$emit('apply-filters', { ...this.localFilters })
    },

    getSexData (sexDistribution, gender) {
      const data = sexDistribution?.[gender]
      if (!data) return { count: 0, percent: '0.0' }
      return data
    },

    getTopAgeGroup (ageDistribution) {
      if (!ageDistribution || ageDistribution.length === 0) return '-'
      const top = ageDistribution.reduce((max, item) =>
        item.count > max.count ? item : max, ageDistribution[0])
      return `${top.group} (${top.count})`
    },

    getTopLocation (topLocations) {
      if (!topLocations || topLocations.length === 0) return '-'
      const location = topLocations[0]
      return location.city === 'Unknown' ? '-' : `${location.city} (${location.count})`
    }
  }
}
</script>

<style lang="less">
@import '../assets/styles/var.less';

.correlations-container {
  @media only screen and (min-width: 0) {
    .search-filters {
      label {
        input {
          width: 180px !important;
        }
        select {
          width: 120px!important;
        }
      }
    }
  }
}
</style>
